import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { getCaptchaToken } from "../utils/recaptcha";
import UserMenu from "../components/UserMenu";
import brandMark from "../assets/logos/g-tower-waves.svg";
import heroLockup from "../assets/logos/horizontal-tower-on-dark.svg";

/**
 * Landing — public marketing/welcome page served at `/welcome`.
 *
 * Variant C ("Stats Wall") from `UI/Landing Wireframes v2.html`. Single-file
 * page by design — sub-components below are inline helpers, not separate
 * modules. Re-factor only when a second page actually reuses a piece.
 *
 * Visual rules (see UI/design-spec-landing.md):
 *   - Tokens only — no literal hex values.
 *   - No new fonts, no radius > 3px, no emoji.
 */

const EM_DASH = "—";

function fmt(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return EM_DASH;
  return Number(n).toLocaleString();
}

/**
 * FAQ copy. KEEP IN SYNC, word for word, with the static #root markup AND the
 * FAQPage JSON-LD in public/index.html — Google requires the structured-data
 * text to match what's visible. Every answer is grounded in the codebase
 * (routes_scheduling.py MIN/MAX_RADIUS, the 250-row export cap in main.py,
 * the Monday pipeline); don't add claims the app can't back.
 */
const FAQ_ITEMS = [
  {
    q: "How do I find new ham radio licensees in my county or area?",
    a: "Create a free account and verify your email, then open Recruitment and search by ZIP code, city and state, or latitude/longitude within a 5 to 50 mile radius. Results appear on a map and in a table, flagged with each license’s recent events — new hams, upgrades, vanity calls, move-ins, renewals, and upcoming expirations.",
  },
  {
    q: "Where does the license data come from, and how often is it updated?",
    a: "All license data comes from the FCC Universal Licensing System (ULS) public weekly amateur radio license database. Every Monday our pipeline downloads the latest file, geocodes addresses with the U.S. Census Bureau geocoder, and recomputes each license’s events from its full FCC history.",
  },
  {
    q: "What do the six license events mean?",
    a: "New Ham: newly licensed. Upgraded: moved up to a higher operator class, such as Technician to General. Vanity CS: switched to a vanity call sign. Addr Change: updated their FCC address, often a move into your area. Renewed: renewed their license for another ten-year term. Exp Soon: license expires within about six months. None of these flags are in the raw FCC data — we compute them from each license’s history.",
  },
  {
    q: "Who is Ham Recruiter for?",
    a: "Amateur radio clubs recruiting new members, ham radio retailers reaching newly licensed and upgraded operators, and license instructors and volunteer examiner (VE) teams following up with recent and soon-to-expire licensees.",
  },
  {
    q: "Is Ham Recruiter free?",
    a: "Creating an account is free, and search, CSV export, and the monthly scheduled email are available once your email is verified.",
  },
  {
    q: "How do I get the lists — CSV or email?",
    a: "Two ways. Export any Recruitment search as a CSV, up to 250 rows per export. Save a schedule — a ZIP code or latitude/longitude center, a 5 to 25 mile radius, and the events you care about — and we email you a CSV of matching hams on the 1st of every month.",
  },
  {
    q: "How is licensee contact information handled?",
    a: "Names and mailing addresses come from public FCC license records. They are shown only to signed-in users with a verified email address, and each CSV export is capped at 250 rows. To stop your monthly emails, delete your schedule on the Scheduling page.",
  },
];

/** Big-stat tile shown in the hero stat wall. */
function BigStat({ value, label, accent }) {
  return (
    <div className="big-stat">
      <div className={"big-stat__n" + (accent ? " big-stat__n--accent" : "")}>
        {fmt(value)}
      </div>
      <div className="big-stat__lbl">{label}</div>
    </div>
  );
}

/** A single event card in the "Six Events" grid. */
function EventCard({ tag, heading, body }) {
  return (
    <div className="event-card">
      <div className="event-card__tag">{tag}</div>
      <h3 className="event-card__heading">{heading}</h3>
      <p className="event-card__body">{body}</p>
    </div>
  );
}

/** A single zig-zag feature row (text + image). */
function FeatureRow({ number, eyebrow, heading, body, chip, image, reversed, onImageClick }) {
  return (
    <div className={"feature-row" + (reversed ? " feature-row--reversed" : "")}>
      <div className="feature-row__text">
        <div className="eyebrow">
          {number} {String.fromCharCode(0x2014)} {eyebrow}
        </div>
        <h3 className="landing-h2">{heading}</h3>
        <p className="landing-p">{body}</p>
        {chip && (
          <span className="feature-row__chip">
            <span className="feature-row__chip-dot" />
            {chip}
          </span>
        )}
      </div>
      <button
        type="button"
        className="feature-row__image-button"
        onClick={() => onImageClick && onImageClick({ src: image, alt: eyebrow })}
        aria-label={`Zoom: ${eyebrow}`}
      >
        <img
          className="feature-row__image"
          src={image}
          alt={eyebrow}
        />
      </button>
    </div>
  );
}

/** Fullscreen lightbox — click backdrop or press Esc to close. */
function Lightbox({ image, onClose }) {
  useEffect(() => {
    if (!image) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [image, onClose]);

  if (!image) return null;

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={image.alt}
      onClick={onClose}
    >
      <button
        type="button"
        className="lightbox__close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
      <img
        className="lightbox__img"
        src={image.src}
        alt={image.alt}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/** Contact form — name / email / subject / message + hidden honeypot. */
function ContactForm() {
  // The app renders before /api/config/public resolves; hold submit until the
  // reCAPTCHA site key is known so enforced captcha doesn't reject the form.
  const { recaptchaSiteKey, loaded: configLoaded } = useConfig();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("Sales");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(""); // honeypot — must stay empty
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in name, email, and a message.");
      return;
    }

    setLoading(true);
    try {
      const captcha_token = await getCaptchaToken(recaptchaSiteKey, "contact");
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, subject, message, company, captcha_token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Could not send message. Please try again.");
        return;
      }
      setInfo("Thanks " + String.fromCharCode(0x2014) + " we'll reply within 1 business day.");
      setName("");
      setEmail("");
      setMessage("");
      // Leave subject + honeypot as-is.
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="landing-contact-form" onSubmit={handleSubmit} noValidate>
      {/* Honeypot — hidden from sighted users + assistive tech. Bots fill it. */}
      <input
        type="text"
        name="company"
        className="landing-contact-form__honeypot"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="auth-gate__field">
        <label className="auth-gate__label">Name</label>
        <input
          className="search-field__input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          autoComplete="name"
          required
        />
      </div>

      <div className="auth-gate__field">
        <label className="auth-gate__label">Email</label>
        <input
          className="search-field__input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
        />
      </div>

      <div className="auth-gate__field landing-contact-form__message">
        <label className="auth-gate__label">Subject</label>
        <select
          className="search-field__input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        >
          <option>Sales</option>
          <option>Feature request</option>
          <option>Bug report</option>
          <option>Other</option>
        </select>
      </div>

      <div className="auth-gate__field landing-contact-form__message">
        <label className="auth-gate__label">Message</label>
        <textarea
          className="search-field__input landing-contact-form__textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What's on your mind?"
          rows={4}
          required
        />
      </div>

      {error && (
        <div className="auth-gate__error landing-contact-form__message">{error}</div>
      )}
      {info && (
        <div className="auth-gate__info landing-contact-form__message">{info}</div>
      )}

      <div className="landing-contact-form__message landing-contact-form__actions">
        <button
          className="btn btn--primary"
          type="submit"
          disabled={loading || !configLoaded}
        >
          {loading ? "Sending..." : "Send Message"}
        </button>
      </div>

      {recaptchaSiteKey && (
        <p className="auth-gate__recaptcha-notice landing-contact-form__message">
          This site is protected by reCAPTCHA and the Google{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{" "}
          apply.
        </p>
      )}
    </form>
  );
}

export default function Landing() {
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !authLoading && !!user;
  const [stats, setStats] = useState({
    total: null,
    new_this_week: null,
    extra_class: null,
    counties_covered: null,
  });
  const [zoomedImage, setZoomedImage] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch("/api/landing-stats");
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setStats({
        total: data.total ?? null,
        new_this_week: data.new_this_week ?? null,
        extra_class: data.extra_class ?? null,
        counties_covered: data.counties_covered ?? null,
      });
    } catch {
      // Leave nulls — UI renders em-dashes per §9.
    }
  }, []);

  // NOTE: the "Updated …" header line previously read /api/stats. That endpoint
  // is now auth-gated (security plan item #1, PO decision 2026-06-25), so it
  // would 401 for anonymous landing visitors — the line was removed entirely.
  // The headline stat wall below is fed by the still-public /api/landing-stats.

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <div className="landing">

      {/* === Header — same shell as the rest of the app === */}
      <header className="app-header">
        <Link to={isAuthed ? "/home" : "/"} className="app-header__brand" aria-label="Ham Recruiter — home">
          <img src={brandMark} alt="" aria-hidden="true" className="app-header__logo" width="26" height="26" />
          <span className="app-header__title">Ham Recruiter</span>
          <span className="app-header__subtitle">FCC ULS</span>
        </Link>
        <nav className="app-header__nav">
          {isAuthed && <Link to="/home" className="app-header__nav-link">Home</Link>}
          {isAuthed && <Link to="/map" className="app-header__nav-link">Map</Link>}
          {isAuthed && <Link to="/recruitment" className="app-header__nav-link">Recruitment</Link>}
          {isAuthed && <Link to="/scheduling" className="app-header__nav-link">Scheduling</Link>}
          {user?.role === "admin" && (
            <Link to="/admin" className="app-header__nav-link">Admin</Link>
          )}
          {/* Server-rendered public pages (SEO Phase 2), not an SPA route, so a
              plain <a>, shown to logged-out visitors too. Mirrored in index.html. */}
          <a href="/new-hams" className="app-header__nav-link">New Hams</a>
        </nav>
        <div className="app-header__meta">
          {/* "Updated …" stat removed per security plan item #1 (/api/stats
              now gated). */}
          <UserMenu />
        </div>
      </header>

      {/* === Hero: outreach-first, six events as the headline === */}
      <section className="landing-section">
        <img src={heroLockup} alt="Ham Recruiter" className="landing-hero-mark" />
        <div className="eyebrow">OUTREACH FOR HAM RADIO CLUBS, RETAILERS & INSTRUCTORS</div>
        <h1 className="landing-h1">
          Six life-cycle events the FCC doesn{"’"}t expose.
        </h1>
        <p className="landing-lead">
          The public ULS dump tells you who has a license. It doesn{"’"}t tell
          you who just got one, who upgraded, who moved into your county, or
          whose ticket is about to expire. We compute all of that {EM_DASH} every
          Monday {EM_DASH} so you can reach the right ham at the right moment.
        </p>
        <div className="landing-btn-row">
          <Link to="/scheduling?mode=register" className="btn btn--primary">
            Sign Up Free &rarr;
          </Link>
          {/* "Browse the Map" CTA removed per security plan item #1 (PO decision
              2026-06-25): /map is now login-only. */}
        </div>
        <div className="landing-stat-wall">
          <BigStat value={stats.total}            label="TOTAL LICENSES" accent />
          <BigStat value={stats.new_this_week}    label="NEW THIS WEEK" />
          <BigStat value={stats.extra_class}      label="AMATEUR EXTRA" />
          <BigStat value={stats.counties_covered} label="COUNTIES COVERED" />
        </div>
        {/* Crawl entry point to /new-hams (requirements B-6). Plain <a>, mirrored in index.html. */}
        <a href="/new-hams" className="landing-stat-link">New hams by state and county &rarr;</a>
      </section>

      {/* === The six events — the actual product === */}
      <section className="landing-section landing-section--alt">
        <div className="eyebrow">THE SIX EVENTS WE DETECT</div>
        <h2 className="landing-h2">
          Computed weekly. Not in the raw FCC data.
        </h2>
        <p className="landing-p">
          Every license is scanned against its full history. When a callsign
          crosses one of these thresholds, we flag it so your next outreach
          list writes itself.
        </p>
        <div className="event-grid">
          <EventCard
            tag="NEW HAM"
            heading="Just got their license"
            body="Congratulate the newest Technicians in your county before anyone else does. The single biggest recruitment moment."
          />
          <EventCard
            tag="UPGRADED"
            heading="Moved up a class"
            body="General and Extra upgrades are the hams hungry for HF gear, mentoring, and contesting clubs."
          />
          <EventCard
            tag="VANITY CS"
            heading="Picked a vanity callsign"
            body={`A vanity call means they care. They are leaning in ${EM_DASH} the right moment to invite them to a club night.`}
          />
          <EventCard
            tag="ADDR CHANGE"
            heading="Just moved into the area"
            body="A licensed ham relocating to your county is an instant-add to the local roster. Welcome them first."
          />
          <EventCard
            tag="EXP SOON"
            heading="License expires within 6 months"
            body="Nudge them through the renewal so the club doesn’t silently lose a member to an expired ticket."
          />
          <EventCard
            tag="RENEWED"
            heading="Just renewed for ten more years"
            body="A renewal signals long-term commitment. Loop them into volunteer roles, VE sessions, or field-day teams."
          />
        </div>
      </section>

      {/* === What you can do — feature rows anchored to the events === */}
      <section className="landing-section">
        <div className="eyebrow">WHAT YOU CAN DO WITH THE EVENTS</div>
        <FeatureRow
          number="01"
          eyebrow="RECRUITMENT"
          heading="Filter by event, by radius, by class."
          body="Show me every New Ham + Upgraded license within 25 miles of 30301, Technician or General, that surfaced this month. The map and table update together."
          chip="6 event filters · ZIP, city, or lat/lon"
          image="/screenshots/recruitment.png"
          onImageClick={setZoomedImage}
        />
        {/* TODO: scheduling.png is a placeholder copy of map.png. Sign in
            locally and capture the active-schedule card at 1440x900. */}
        <FeatureRow
          reversed
          number="02"
          eyebrow="SCHEDULING"
          heading="Save the events you care about. Get a CSV every Monday."
          body="Pick your events (New Ham, Upgraded, Vanity CS, Addr Change, Exp Soon, Renewed), pick a radius, and we email you only the hams who hit those triggers that week. No clicking, no exporting."
          chip="Mondays · 6am ET · CSV attachment"
          image="/screenshots/scheduling.png"
          onImageClick={setZoomedImage}
        />
        <FeatureRow
          number="03"
          eyebrow="HOW THE EVENTS GET COMPUTED"
          heading="Full license history, scanned every week."
          body="Each Monday we ingest the FCC ULS weekly dump, geocode addresses against Census TIGER, and replay the history log to flag new / upgrade / vanity / address-change / renewal / expiring transitions. None of those flags exist in the raw data."
          image="/screenshots/map.png"
          onImageClick={setZoomedImage}
        />
      </section>

      {/* === FAQ — copy mirrored in public/index.html (#root + FAQPage JSON-LD) === */}
      <section className="landing-section landing-section--alt landing-faq" aria-labelledby="landing-faq-heading">
        <div className="eyebrow">FAQ</div>
        <h2 id="landing-faq-heading" className="landing-h2">Frequently asked questions</h2>
        <div className="landing-faq__list">
          {FAQ_ITEMS.map((item) => (
            <details key={item.q} className="landing-faq__item">
              <summary className="landing-faq__summary">
                <h3 className="landing-faq__question">{item.q}</h3>
              </summary>
              <p className="landing-faq__answer">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* === Contact === */}
      <section className="landing-section">
        <div className="landing-contact">
          <div>
            <div className="eyebrow">CONTACT</div>
            <h2 className="landing-h2">
              Questions?{" "}
              <span className="landing-contact__email">support@hamrecruiter.com</span>
            </h2>
            <p className="landing-p">
              Or send a note {EM_DASH} we reply within 1 business day.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer__copy">
          <img src={brandMark} alt="" aria-hidden="true" className="landing-footer__logo" />
          <span>{"© 2026 HAM RECRUITER " + EM_DASH + " DATA FROM FCC ULS"}</span>
        </div>
        <div className="landing-footer__links">
          <a href="/new-hams">NEW HAMS BY STATE</a>
          <Link to="/map">MAP</Link>
          <Link to="/recruitment">RECRUITMENT</Link>
          <Link to="/scheduling">SIGN IN</Link>
        </div>
      </footer>

      <Lightbox image={zoomedImage} onClose={() => setZoomedImage(null)} />
    </div>
  );
}
